import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarCheck, Phone, Rotate3d } from "lucide-react";
import { api, fmtNumber, fmtPrice } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { useLang } from "../context/LanguageContext";

const ListingDetail = () => {
  const { id } = useParams();
  const { openBooking } = useBooking();
  const { t } = useLang();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    api.get(`/listings/${id}`)
      .then(({ data }) => setListing(data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-7xl px-4 pb-24 pt-40 text-center sm:px-8">
        <p className="font-serif text-3xl font-bold text-navy">{t("dt.notFound")}</p>
        <Link to="/listings" data-testid="detail-back-link" className="mt-6 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-crimson">
          {t("dt.backTo")}
        </Link>
      </div>
    );
  }

  if (!listing) {
    return <div className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-8"><div className="aspect-video animate-pulse bg-paper-cream" /></div>;
  }

  const forSale = listing.status === "for_sale";

  const specs = [];
  if (listing.beds != null) specs.push([listing.beds, t("dt.beds")]);
  if (listing.baths != null) specs.push([listing.baths, t("dt.baths")]);
  if (listing.sqft != null) specs.push([fmtNumber(listing.sqft), t("dt.sqft")]);

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-32 sm:px-8" data-testid="listing-detail-page">
      <Link to="/listings" data-testid="detail-back-link" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-crimson">
        <ArrowLeft size={14} /> {t("dt.back")}
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="mt-8 grid gap-12 lg:grid-cols-[1.35fr_0.65fr]"
      >
        <div>
          <div className="engraved-frame bg-white p-3 shadow-elevated">
            <img
              data-testid="detail-main-image"
              src={listing.images[active]}
              alt={listing.title}
              className="aspect-[16/10] w-full object-cover"
            />
          </div>
          {listing.images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {listing.images.map((src, i) => (
                <button
                  key={i}
                  data-testid={`detail-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`overflow-hidden border-2 transition-colors ${i === active ? "border-gold" : "border-transparent"}`}
                >
                  <img src={src} alt="" className="h-16 w-24 object-cover" />
                </button>
              ))}
            </div>
          )}

          {listing.video_url && (
            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{t("dt.walkthrough")}</p>
              {listing.video_url.includes("facebook.com") ? (
                <div className="mt-3 flex justify-center border border-engraved bg-navy p-3">
                  <iframe
                    data-testid="detail-video"
                    src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(listing.video_url)}&show_text=false`}
                    className="aspect-[9/16] w-full max-w-sm"
                    allowFullScreen
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    title="Video walkthrough"
                  />
                </div>
              ) : (
                <video
                  data-testid="detail-video"
                  src={listing.video_url}
                  controls
                  playsInline
                  preload="metadata"
                  poster={listing.images[1] || listing.images[0]}
                  className="mt-3 w-full border border-engraved bg-navy"
                />
              )}
            </div>
          )}

          <h2 className="mt-10 font-serif text-xl font-bold text-navy">{t("dt.about")}</h2>
          <p data-testid="detail-description" className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            {listing.description}
          </p>
          <p className="mt-8 border-t border-engraved pt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            {t("dt.listedBy")}
          </p>
        </div>

        <aside className="h-fit border border-engraved bg-white p-8 shadow-elevated lg:sticky lg:top-28">
          <span className={`inline-block px-3 py-1.5 font-cinzel text-[10px] font-bold uppercase tracking-[0.2em] ${forSale ? "bg-crimson text-paper" : listing.status === "pending" ? "bg-gold text-navy" : "bg-navy text-paper"}`}>
            {forSale ? t("card.forSale") : listing.status === "pending" ? t("card.pending") : t("card.sold")}
          </span>
          {listing.tag && <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-crimson">{listing.tag}</p>}
          <p data-testid="detail-price" className="mt-4 font-serif text-4xl font-bold text-navy">{fmtPrice(listing.price)}</p>
          {listing.sqft ? (
            <p className="mt-1 font-mono text-[11px] text-slate-500">
              ${Math.round(listing.price / listing.sqft)} {t("dt.perSqft")}
            </p>
          ) : null}
          <h1 className="mt-5 font-serif text-xl font-semibold text-ink">{listing.title}</h1>
          <p className="text-sm text-slate-500">{listing.city}, {listing.state} {listing.zip}</p>
          {listing.agent && (
            <p data-testid="detail-agent" className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
              {t("dt.agent")}: {listing.agent}
            </p>
          )}

          {listing.open_house && (
            <div data-testid="detail-open-house" className="mt-6 flex items-center gap-3 border border-gold/50 bg-gold/10 px-4 py-3.5">
              <CalendarCheck size={16} className="shrink-0 text-gold" />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{t("oh.label")}</p>
                <p className="mt-0.5 text-sm font-semibold text-navy">{listing.open_house}</p>
              </div>
            </div>
          )}

          {specs.length > 0 && (
            <div
              className="mt-6 grid divide-x divide-engraved border-y border-engraved py-4 text-center"
              style={{ gridTemplateColumns: `repeat(${specs.length}, minmax(0, 1fr))` }}
            >
              {specs.map(([v, l]) => (
                <div key={l}>
                  <p className="font-serif text-lg font-bold text-navy">{v}</p>
                  <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{l}</p>
                </div>
              ))}
            </div>
          )}

          {forSale && (
            <button
              data-testid="listing-detail-schedule-button"
              onClick={() => openBooking({ listing, service: "tour" })}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-navy px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-crimson"
            >
              <CalendarCheck size={15} /> {t("dt.schedule")}
            </button>
          )}
          {listing.tour_url && (
            <a
              href={listing.tour_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="listing-detail-tour-button"
              className="mt-3 flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-gold"
            >
              <Rotate3d size={15} /> {t("dt.tour3d")}
            </a>
          )}
          <a
            href="tel:+17022414761"
            data-testid="listing-detail-call-button"
            className="mt-3 flex w-full items-center justify-center gap-2 border border-navy px-6 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-navy transition-colors duration-300 hover:bg-navy hover:text-paper"
          >
            <Phone size={15} /> 702 · 241 · 4761
          </a>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
            {t("dt.ask")}
          </p>
        </aside>
      </motion.div>
    </div>
  );
};

export default ListingDetail;
